import React, { useCallback, useEffect } from 'react';
import { Box } from '@mui/material';
import { useForm } from '@travelity/form';
import { array, object, string } from 'yup';
import {
    convertFullNameToNameDto,
    memberToDetailsForm,
    TeamMember,
    useUpdateTeamMember,
} from '@travelity/api';
import { SideFormAccordion } from '../../../components/side-form-accordion';
import { useSideFormAccordion } from '../../../components/side-form-accordion/side-form-accordion.hooks';
import MemberPersonalDetailsForm from './member-personal-details';
import { SidebarContent } from '../../../components/sidebar-content';

export interface MemberPreviewProps {
    member: TeamMember;
    teamId: string;
    isEditable?: boolean;
    onClose: () => void;
}

const detailsSchema = object().shape({
    fullName: string().required(),
    numbers: array(),
    company: string(),
});

export interface MemberDetailsForm {
    fullName: string;
    numbers: { value: string }[];
    company?: string;
}

const MemberPreview: React.FC<MemberPreviewProps> = ({
    member,
    teamId,
    isEditable,
    onClose,
}) => {
    const { view, openTab, isEdit } = useSideFormAccordion();

    const { mutate: updateMember, isLoading } = useUpdateTeamMember({
        onSuccess: () => {
            view();
        },
    });
    const onSubmit = useCallback(
        (data: MemberDetailsForm) => {
            const oldData = memberToDetailsForm(member);
            if (
                oldData.numbers.toString() === data.numbers.toString() &&
                oldData.fullName === data.fullName &&
                oldData.company === data.company
            ) {
                view();
                return;
            }
            updateMember({
                teamId,
                id: member.id,
                name: data.fullName
                    ? convertFullNameToNameDto(data.fullName)
                    : undefined,
                phoneNumbers:
                    data.numbers
                        ?.map(({ value }) => value)
                        .map(number => ({
                            calling_code: parseInt(number.split(' ')[0], 10),
                            number: parseInt(number.split(' ')[1], 10),
                        })) || [],
                company: data.company,
            });
        },
        [member]
    );

    const {
        Form: DetailsForm,
        reset: resetDetailsForm,
        submit,
        // formState: { errors: travelersErrors },
    } = useForm({
        defaultValues: memberToDetailsForm(member),
        onSubmit,
        mode: 'onChange',
        schema: detailsSchema,
    });

    const onSave = () => {
        submit();
    };
    const onCancel = () => {
        resetDetailsForm(memberToDetailsForm(member));
        view();
    };

    useEffect(() => {
        resetDetailsForm(memberToDetailsForm(member));
    }, [member]);

    return (
        <SidebarContent title="Member Preview" onClose={onClose}>
            <Box
                sx={{
                    '&&& .MuiAccordionSummary-root': {
                        borderBottom: '1px solid #DFE1ED',
                    },
                    '&&& .MuiAccordionDetails-root': {
                        py: 3,
                    },
                }}
            >
                <SideFormAccordion
                    id="personal-details"
                    title="Personal Details"
                    isEditable={isEditable}
                    isSaving={isLoading}
                    onSave={onSave}
                    onCancel={onCancel}
                >
                    <DetailsForm>
                        <MemberPersonalDetailsForm
                            preview={!isEdit || openTab !== 'personal-details'}
                        />
                    </DetailsForm>
                </SideFormAccordion>
            </Box>
        </SidebarContent>
    );
};

export default React.memo(MemberPreview);
